// AttachmentCarousel.tsx
import React, { useRef, useState, useEffect, useCallback } from "react";
import { getFileTypeIcon } from "../../utils/functions/file-icon-extractor";
import CircularLoader from "../../components/Loader/CircularLoader";
import ChevronLeftIcon from "../../assets/svg/chevron-left-icon";
import ChevronRightIcon from "../../assets/svg/chevron-right-icon";
import { AttachmentItem } from "./AttachmentInput";

type Props = {
  items: AttachmentItem[];
  onPreview: (item: AttachmentItem, index?: number) => void;
  onRemove: (id: string) => void;
  hideDeleteButton?: boolean;
  variant?: "default" | "compact";
};

function fileExt(name = "") {
  const m = name.split(".");
  return m.length > 1 ? m[m.length - 1].toLowerCase() : "";
}

function isImageMime(mime?: string) {
  return !!mime && mime.startsWith("image/");
}

const AttachmentInputCarousel: React.FC<Props> = ({
  items,
  onPreview,
  onRemove,
  hideDeleteButton = false,
  variant = "default",
}) => {
  const isCompact = variant === "compact";
  const carouselRef = useRef<HTMLDivElement>(null);
  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);

  const checkScrollButtons = useCallback(() => {
    if (!carouselRef.current) {
      setShowLeftButton(false);
      setShowRightButton(false);
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = carouselRef.current;
    const canScrollLeft = scrollLeft > 0;
    const canScrollRight = scrollLeft < scrollWidth - clientWidth - 1; // -1 for rounding errors
    const isOverflowing = scrollWidth > clientWidth;

    setShowLeftButton(isOverflowing && canScrollLeft);
    setShowRightButton(isOverflowing && canScrollRight);
  }, []);

  useEffect(() => {
    checkScrollButtons();
    const carousel = carouselRef.current;
    if (!carousel) return;

    const handleScroll = () => {
      checkScrollButtons();
    };

    carousel.addEventListener("scroll", handleScroll);
    const resizeObserver = new ResizeObserver(() => {
      checkScrollButtons();
    });
    resizeObserver.observe(carousel);

    return () => {
      carousel.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, [items, checkScrollButtons]);

  function scrollCarousel(dir: "left" | "right") {
    if (!carouselRef.current) return;
    const width = carouselRef.current.clientWidth;
    const delta = Math.max(120, Math.floor(width * 0.6));
    carouselRef.current.scrollBy({
      left: dir === "left" ? -delta : delta,
      behavior: "smooth",
    });
    setTimeout(() => checkScrollButtons(), 100);
  }

  if (items.length === 0) {
    return null;
  }

  const navButtonClass = isCompact
    ? "shrink-0 w-[26px] h-[26px] rounded-full bg-white/80 dark:bg-white/10 backdrop-blur-[20px] border border-border-gray dark:border-border-gray-dk flex items-center justify-center shadow-[10px_10px_60px_0px_rgba(0,0,0,0.2)] cursor-pointer transition-colors hover:bg-white dark:hover:bg-white/20"
    : "flex-shrink-0 w-8 h-8 rounded-full bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk flex items-center justify-center hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors";

  return (
    <div
      className={
        isCompact
          ? "relative flex items-center gap-2"
          : "relative flex items-center gap-2 p-2 bg-transparent dark:bg-transparent"
      }
    >
      {showLeftButton && (
        <button
          type="button"
          onClick={() => scrollCarousel("left")}
          aria-label="Scroll left"
          className={navButtonClass}
        >
          {isCompact ? (
            <ChevronLeftIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
          ) : (
            <span className="text-gray-600 dark:text-gray-400 text-lg">‹</span>
          )}
        </button>
      )}
      <div
        ref={carouselRef}
        className={
          isCompact
            ? "flex gap-2 overflow-x-auto flex-1"
            : "flex gap-2 overflow-x-auto scrollbar-hide flex-1 py-1 px-1"
        }
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        {items.map((it, index) => (
          <div
            key={it.id}
            className={
              isCompact
                ? `shrink-0 w-[60px] h-[60px] rounded-[8px] overflow-hidden relative group ${
                    it.previewUrl && isImageMime(it.mime)
                      ? "bg-[#fef0f0]"
                      : "bg-border-gray dark:bg-border-gray-dk"
                  } ${it.status === "uploading" ? "cursor-wait" : "cursor-pointer"}`
                : `flex-shrink-0 w-20 h-20 rounded-lg bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk relative group p-1 ${
                    it.status === "uploading" ? "cursor-wait" : "cursor-pointer"
                  }`
            }
            onClick={() => it.status !== "uploading" && onPreview(it, index)}
          >
            <div
              className={
                isCompact
                  ? "w-full h-full flex items-center justify-center overflow-hidden relative"
                  : "w-full h-full flex items-center justify-center rounded-lg overflow-hidden relative"
              }
            >
              {it.previewUrl && isImageMime(it.mime) ? (
                <img
                  src={it.previewUrl}
                  alt={it.name}
                  className={`object-cover w-full h-full ${
                    isCompact ? "" : "rounded"
                  } ${it.status === "uploading" ? "opacity-50" : ""}`}
                  loading="lazy"
                />
              ) : (
                <img
                  src={getFileTypeIcon(it.name)}
                  alt={`${fileExt(it.name)} file icon`}
                  className={`${isCompact ? "w-6 h-6" : "w-10 h-10"} object-contain ${
                    it.status === "uploading" ? "opacity-50" : ""
                  }`}
                  loading="lazy"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      getFileTypeIcon("");
                  }}
                />
              )}
              {it.status === "uploading" && (
                <div
                  className={`absolute inset-0 flex items-center justify-center bg-gray-50/80 dark:bg-gray-800/80 ${
                    isCompact ? "" : "rounded-lg"
                  }`}
                >
                  <CircularLoader
                    className={`${isCompact ? "w-5 h-5" : "w-6 h-6"} text-verba-blue dark:text-verba-blue-dk`}
                  />
                </div>
              )}
            </div>
            {isCompact && it.status !== "uploading" && (
              <div className="pointer-events-none absolute inset-0 bg-image-attachments-overlay rounded-[8px] opacity-0 group-hover:opacity-100 transition-opacity z-5" />
            )}
            {!hideDeleteButton && it.status !== "uploading" && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(it.id);
                }}
                className={
                  isCompact
                    ? "absolute top-[3px] right-[3px] w-[14px] h-[14px] flex items-center justify-center text-white text-[9px] opacity-0 group-hover:opacity-100 transition-opacity z-10"
                    : "absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gray-50 dark:bg-gray-800 border border-border-gray dark:border-border-gray-dk text-black dark:text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs z-10 shadow-sm"
                }
                aria-label="Remove attachment"
              >
                ✕
              </button>
            )}
          </div>
        ))}
      </div>
      {showRightButton && (
        <button
          type="button"
          onClick={() => scrollCarousel("right")}
          aria-label="Scroll right"
          className={navButtonClass}
        >
          {isCompact ? (
            <ChevronRightIcon className="w-[14px] h-[14px] text-verba-indigo dark:text-verba-indigo-dk" />
          ) : (
            <span className="text-gray-600 dark:text-gray-400 text-lg">›</span>
          )}
        </button>
      )}
    </div>
  );
};

AttachmentInputCarousel.displayName = "AttachmentInputCarousel";

export default AttachmentInputCarousel;
